import React, { useMemo } from "react";
import {
    numberFormatterForDecimalInMillion,
    numberFormatterForDecimalInMNew,
} from "../../utils/formatter";

export type TierDetail = {
    tierType: string;
    startValue: number;
    endValue: number;
    benefitPercentage: number;
    progress: number;
    projection: string;
};

export type ProgressRow = {
    type: "Parents" | "Others";
    district: string;
    supplier: string;
    commodity: string;
    category: string;
    growthTierType: string;
    backToZero: string;
    dollarPoundToNextTier: number;
    nextTierBenefit: number;
    projectedPurchaseEnd: number | null;
    currentPurchase: number;
    currentRebatePurchases: string | number;
    tierDetails: TierDetail[];
};

type ProgressBarCellProps = {
    row: ProgressRow;
    height?: number;
    isTopValueView?: boolean;
    width?: string;
    isParent?: boolean;
    decimal?: number;
};

type TopValueLabel = {
    leftPct: number;
    value: number;
    variant: "base" | "max" | "projectStart" | "projectEnd";
};

const pctLabel = (p: number) => `${p}%`;
const clamp = (n: number, min: number, max: number) =>
    Math.max(min, Math.min(max, n));

const normalizeGrowthType = (t: string) => t?.toLowerCase?.() ?? "";

const isBaseTierType = (tierType: string) => {
    const normalized = tierType?.trim().toLowerCase();
    return normalized === "base tier" || normalized === "base rebate";
};

const getParentFilledBoxes = (tiers: TierDetail[]) => {
    const boxCount = Math.max(tiers.length - 1, 0);
    if (!boxCount) return 0;

    const progressSum = tiers.reduce((sum, tier) => {
        if (isBaseTierType(tier.tierType)) return sum;
        return sum + (Number(tier.progress) || 0);
    }, 0);

    if (progressSum <= 0) return 0;

    return clamp(Math.round(progressSum), 0, boxCount);
};

/**
 * Map a value into 0-100% using box-based scaling.
 */
const valueToBoxPercent = (tiers: TierDetail[], value: number): number => {
    if (!tiers.length) return 0;
    if (tiers.length === 1) return 100;

    const firstStart = tiers[0].startValue;
    const lastStart = tiers[tiers.length - 1].startValue;
    const boxCount = tiers.length - 1;
    const boxWidth = 100 / boxCount;

    if (value <= firstStart) return 0;
    if (value >= lastStart) return 100;

    let boxIdx = 0;
    let boxStart = firstStart;
    let boxEnd = lastStart;

    for (let i = 0; i < boxCount; i++) {
        const s = tiers[i].startValue;
        const e = tiers[i + 1].startValue;
        if (value >= s && value < e) {
            boxIdx = i;
            boxStart = s;
            boxEnd = e;
            break;
        }
    }

    const localDenom = boxEnd - boxStart || 1;
    const localFraction = clamp((value - boxStart) / localDenom, 0, 1);

    return (boxIdx + localFraction) * boxWidth;
};

const getTierColor = (tiers: TierDetail[], value: number) => {
    if (!tiers.length) return "#CFCFCF";

    // A single BASE REBATE tier is a flat rebate that's always fully met,
    // so treat it as achieved (green).
    if (tiers.length === 1) {
        return "#64C850";
    }

    if (tiers.length === 2) {
        return "#64C850";
    }

    const lastBoxIdx = tiers.length - 2;
    const lastTierStart = tiers[tiers.length - 1].startValue;
    let boxIdx = 0;

    if (value < tiers[0].startValue) {
        boxIdx = 0;
    } else if (value >= lastTierStart) {
        boxIdx = lastBoxIdx;
    } else {
        for (let i = 0; i <= lastBoxIdx; i++) {
            const start = tiers[i].startValue;
            const end = tiers[i + 1].startValue;
            if (value >= start && value < end) {
                boxIdx = i;
                break;
            }
        }
    }

    if (boxIdx === 0) return "#CC5500";
    if (boxIdx === lastBoxIdx) return "#64C850";
    return "#FFBF00";
};

const ProgressBarCell: React.FC<ProgressBarCellProps> = ({
    row,
    height,
    isTopValueView = false,
    width,
    decimal = 1,
}) => {
    const tiers = row.tierDetails ?? [];
    // For a single BASE REBATE tier there is no "next tier" threshold, so the
    // last tier's startValue is 0. In that case show the actual spend instead.
    const totalEndValue =
        tiers.length === 1
            ? row.currentPurchase ?? 0
            : tiers[tiers.length - 1]?.startValue ?? 0;
    const isParent = row.type === "Parents";

    const parentBoxCount = Math.max(tiers.length - 1, 0);

    const parentFilledBoxes = useMemo(() => {
        if (!isParent) return 0;
        return getParentFilledBoxes(tiers);
    }, [isParent, tiers]);

    const progressPct = useMemo(() => {
        if (isParent) {
            if (!parentBoxCount || parentFilledBoxes <= 0) return 0;
            return (parentFilledBoxes / parentBoxCount) * 100;
        }

        return valueToBoxPercent(tiers, row.currentPurchase);
    }, [isParent, parentBoxCount, parentFilledBoxes, tiers, row.currentPurchase]);

    const projectedPct = useMemo(
        () =>
            row.projectedPurchaseEnd != null
                ? valueToBoxPercent(tiers, row.projectedPurchaseEnd)
                : 0,
        [tiers, row.projectedPurchaseEnd]
    );

    const achievedColor = isParent ? "#64C850" : getTierColor(tiers, row.currentPurchase);

    const topValueLabels = useMemo<TopValueLabel[]>(() => {
        if (!tiers.length) return [];

        const count = tiers.length;
        const step = count > 1 ? 100 / (count - 1) : 0;

        const labels: TopValueLabel[] = tiers.map((tier, idx) => ({
            leftPct: idx * step,
            value: tier.startValue,
            variant: idx === 0 ? "base" : idx === count - 1 ? "max" : "base",
        }));

        if (row.currentPurchase < totalEndValue) {
            labels.push({
                leftPct: valueToBoxPercent(tiers, row.currentPurchase),
                value: row.currentPurchase,
                variant: "projectStart",
            });
        }

        if (row.projectedPurchaseEnd != null) {
            const projectEndLeft = valueToBoxPercent(tiers, row.projectedPurchaseEnd);
            if (
                row.projectedPurchaseEnd < totalEndValue &&
                row.currentPurchase < row.projectedPurchaseEnd
            ) {
                labels.push({
                    leftPct: projectEndLeft,
                    value: row.projectedPurchaseEnd,
                    variant: "projectEnd",
                });
            }
        }

        return labels;
    }, [tiers, row.currentPurchase, row.projectedPurchaseEnd, totalEndValue]);

    const labelPositions = useMemo(() => {
        if (!tiers.length) return [];
        const count = tiers.length;
        const step = count > 1 ? 100 / (count - 1) : 0;

        return tiers.map((tier, idx) => ({
            leftPct: idx * step,
            value: tier.benefitPercentage,
            name: tier.tierType,
        }));
    }, [tiers]);

    const boxSeparators = useMemo(() => {
        if (tiers.length < 2) return [];
        const count = tiers.length;
        const step = 100 / (count - 1);

        return Array.from({ length: count - 2 }, (_, i) => ({
            leftPct: (i + 1) * step,
        }));
    }, [tiers]);

    const topLabelClass = (variant: TopValueLabel["variant"]) => {
        switch (variant) {
            case "max":
                return "text-gray-800 font-semibold -top-0 -translate-x-1/2";
            case "projectStart":
                return "text-orange-500 font-semibold top-5 translate-x-1/5";
            case "projectEnd":
                return "text-gray-500 font-semibold top-10 translate-y-1/8";
            default:
                return "";
        }
    };

    const growthType = normalizeGrowthType(row.growthTierType);
    const isDollar = growthType.startsWith("dollars");
    const isPound = growthType.startsWith("pounds");

    return (
        <div className="flex w-full items-center gap-0">
            <div className="relative -z-0 items-center gap-0" style={{ width: width ?? "100%" }}>
                {!isParent && isTopValueView && (
                    <div className="relative mb-1 h-4 text-[10px] text-gray-700">
                        {topValueLabels.map((label, idx) => (
                            <span
                                key={`top-${label.value}-${label.variant}-${idx}`}
                                className={`z-10 absolute font-bold text-[0.8vw] whitespace-nowrap ${topLabelClass(
                                    label.variant
                                )}`}
                                style={{ left: `${label.leftPct}%` }}
                            >
                                {isDollar && "$"}
                                {numberFormatterForDecimalInMNew(label.value, decimal)}
                                {isPound && " lbs."}
                            </span>
                        ))}
                    </div>
                )}

                <div
                    className="relative w-full rounded-md border-2 border-gray-400 bg-gray-100 overflow-hidden"
                    style={{ height: height ?? 32 }}
                >
                    {!isParent && projectedPct > 0 && tiers.length > 0 && row.currentPurchase > 0 && (
                        <div
                            className="absolute left-0 top-0 h-full"
                            style={{
                                width: `${projectedPct}%`,
                                backgroundColor: "#CFCFCF",
                                backgroundImage:
                                    "linear-gradient(135deg, rgba(255,255,255,0.55) 25%, transparent 25%, transparent 50%, rgba(255,255,255,0.55) 50%, rgba(255,255,255,0.35) 75%, transparent 75%, transparent)",
                                backgroundSize: "10px 10px",
                                borderLeft: isTopValueView ? "2px dotted #9ca3af" : "none",
                                borderRight: isTopValueView ? "2px dotted #9ca3af" : "none",
                                zIndex: 1,
                            }}
                        />
                    )}

                    {!isParent && tiers.length > 0 && row.currentPurchase > 0 && <div
                        className="absolute left-0 top-0 h-full"
                        style={{
                            width: `${progressPct}%`,
                            backgroundColor: achievedColor,
                            backgroundImage:
                                "linear-gradient(135deg, rgba(255,255,255,0.5) 25%, transparent 25%, transparent 50%, rgba(255,255,255,0.5) 50%, rgba(255,255,255,0.35) 75%, transparent 75%, transparent)",
                            backgroundSize: "10px 10px",
                            borderRight: isTopValueView ? "2px dotted #9ca3af" : "none",
                            zIndex: 2,
                        }}
                    />}

                    {isParent && <div
                        className="absolute left-0 top-0 h-full"
                        style={{
                            width: `${progressPct}%`,
                            backgroundColor: achievedColor,
                            backgroundImage:
                                "linear-gradient(135deg, rgba(255,255,255,0.5) 25%, transparent 25%, transparent 50%, rgba(255,255,255,0.5) 50%, rgba(255,255,255,0.35) 75%, transparent 75%, transparent)",
                            backgroundSize: "10px 10px",
                            borderRight: isTopValueView ? "2px dotted #9ca3af" : "none",
                            zIndex: 2,
                        }}
                    />}

                    {boxSeparators.map((sep, idx) => (
                        <div
                            key={`sep-${idx}`}
                            className="absolute top-0 h-full w-[2px] bg-gray-500"
                            style={{ left: `${sep.leftPct}%`, zIndex: 3 }}
                        />
                    ))}
                </div>

                <div
                    className={`relative mt-0 h-4 text-[10px] text-gray-700`}
                >
                    {labelPositions.map((lp, idx) => (
                        <span
                            key={`${lp.value}-${idx}`}
                            className="absolute -translate-x-1/2 whitespace-nowrap"
                            style={{ left: `${lp.leftPct}%` }}
                            title={lp.name}
                        >
                            {pctLabel(lp.value)}
                        </span>
                    ))}
                </div>
            </div>

            {!isParent && !isTopValueView && (
                <span className="items-center font-bold whitespace-nowrap ml-1 mb-4">
                    {isDollar && "$"}
                    {numberFormatterForDecimalInMNew(totalEndValue, 2)}
                    {isPound && " lbs."}
                </span>
            )}

            {isParent && (
                <span className="items-center font-bold whitespace-nowrap ml-1 mb-4">
                    {getParentFilledBoxes(tiers)}/{tiers.length - 1} Goals
                </span>
            )}
        </div>
    );
};

export default ProgressBarCell;